import { DiscountUpdateEvent } from '@theme/events';
import * as rules from '@theme/discount-coupon-rules';
import { blockDiscountyardRequests, unblockDiscountyardRequests } from '@theme/discountyard-request-blocker';

/**
 * Cupom do carrinho da adição facilitada (overlay mobile + drawer desktop).
 *
 * Mesmo modelo e mesmas regras do /cart (assets/cart-discount-orchestrator.js,
 * cart-discount.js, cart-discount-manager.js), que funcionam:
 * - um campo próprio roteia o código: nativo (Shopify) ou DiscountYard (DY);
 * - nativo vai por /cart/update.js e se acumula com os nativos já aplicados
 *   (chips renderizados no servidor, snippets/zrzs-quick-add-cart-content.liquid);
 * - DY vai pro widget do próprio app (mesma operação do orchestrator), que
 *   fica num host escondido (snippets/zrzs-quick-add-discountyard-host.liquid).
 *   As tags e mensagens do app são espelhadas no painel: cupom aplicado vira
 *   pílula dentro do campo (mesma pílula do nativo), remover clica no "x" do
 *   app, e a mensagem de erro do app aparece no erro do campo.
 *
 * Regras (@theme/discount-coupon-rules) e bloqueio das requisições do DY
 * (@theme/discountyard-request-blocker) são os mesmos módulos do /cart.
 */

/** Mensagens que só existem no painel (o resto vem das regras / traduções do tema). */
const PANEL_MESSAGES = {
  alreadyApplied: 'Este cupom já está aplicado.',
  discountYardUnavailable:
    'Não foi possível aplicar o cupom agora. Tente novamente em instantes.',
  requestFailed: 'Não foi possível aplicar o cupom agora. Tente novamente.',
  removeAriaLabel: 'Remover cupom',
};

/** Mesmo tempo do orchestrator: o resultado do DY aparece na própria UI dele. */
const DISCOUNTYARD_SPINNER_MS = 3000;

// ── Widget do DiscountYard (papel do cart-discount-manager) ────────────

/** @returns {HTMLElement | null} */
function getDiscountYardContainer() {
  return document.getElementById('stack-discounts-container');
}

/**
 * Avisa os painéis quando o widget do DY muda (tag aplicada/removida,
 * mensagem). Observa o body só enquanto o container não existe; achado,
 * observa o container (conteúdo) e o pai dele (se o app trocar o container,
 * volta a procurar pelo body).
 * @type {Set<() => void>}
 */
const discountYardListeners = new Set();
/** @type {MutationObserver | null} */
let discountYardBodyObserver = null;
/** @type {MutationObserver | null} */
let discountYardContainerObserver = null;
/** @type {MutationObserver | null} */
let discountYardParentObserver = null;
/** @type {HTMLElement | null} */
let observedDiscountYardContainer = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let discountYardNotifyTimer = null;

function notifyDiscountYardListeners() {
  if (discountYardNotifyTimer) clearTimeout(discountYardNotifyTimer);
  discountYardNotifyTimer = setTimeout(() => {
    keepDiscountYardHostOutOfTabOrder();
    discountYardListeners.forEach(listener => listener());
  }, 50);
}

/**
 * O widget fica fora da tela no host escondido (aria-hidden) — tira os
 * controles dele da ordem do Tab, senão o teclado cai num campo invisível.
 * Reaplicado a cada mudança porque o app recria os elementos. Cliques e
 * submits programáticos (submitToDiscountYard, "x" das tags) seguem funcionando.
 */
function keepDiscountYardHostOutOfTabOrder() {
  const container = getDiscountYardContainer();
  if (!container?.closest('.zrzs-quick-add-discountyard-host')) return;
  container
    .querySelectorAll('a[href], button, input, select, textarea, [tabindex]')
    .forEach(element => element.setAttribute('tabindex', '-1'));
}

function waitForDiscountYardContainer() {
  if (discountYardBodyObserver) return;
  discountYardBodyObserver = new MutationObserver(() => {
    if (getDiscountYardContainer()) observeDiscountYardContainer();
  });
  discountYardBodyObserver.observe(document.body, { childList: true, subtree: true });
}

function observeDiscountYardContainer() {
  const container = getDiscountYardContainer();
  if (!container) {
    waitForDiscountYardContainer();
    return;
  }
  if (container === observedDiscountYardContainer) return;

  discountYardBodyObserver?.disconnect();
  discountYardBodyObserver = null;
  discountYardContainerObserver?.disconnect();
  discountYardParentObserver?.disconnect();

  observedDiscountYardContainer = container;
  discountYardContainerObserver = new MutationObserver(notifyDiscountYardListeners);
  discountYardContainerObserver.observe(container, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['class', 'data-value', 'data-is-automatic'],
  });

  // Container removido/trocado pelo app: volta a procurar.
  if (container.parentElement) {
    discountYardParentObserver = new MutationObserver(() => {
      if (container.isConnected) return;
      discountYardContainerObserver?.disconnect();
      discountYardParentObserver?.disconnect();
      observedDiscountYardContainer = null;
      notifyDiscountYardListeners();
      observeDiscountYardContainer();
    });
    discountYardParentObserver.observe(container.parentElement, { childList: true });
  }

  notifyDiscountYardListeners();
}

/**
 * @param {() => void} listener
 * @returns {() => void} cancela a inscrição
 */
function watchDiscountYard(listener) {
  discountYardListeners.add(listener);
  observeDiscountYardContainer();
  return () => discountYardListeners.delete(listener);
}

/** Mensagem que o próprio DY mostra (ex.: cupom inválido), ou ''. */
function getDiscountYardMessage() {
  const message = document.querySelector(
    '#stack-discounts-container .stack-discounts-message.message--show .message-text'
  );
  return (message?.textContent || '').trim();
}

/**
 * Remover pela pílula: clica no "x" do próprio app (como o usuário faria no /cart).
 * @param {string} code
 */
function removeDiscountYardCoupon(code) {
  const codeUpper = code.toUpperCase();
  const tag = getManualDiscountYardTags().find(
    manualTag => (manualTag.getAttribute('data-value') || '').toUpperCase() === codeUpper
  );
  const closeButton = tag?.querySelector('.stack-discounts-close-tag');
  if (closeButton instanceof HTMLElement) closeButton.click();
}

/** Cupons manuais (não automáticos) aplicados no DY. */
function getManualDiscountYardTags() {
  return Array.from(
    document.querySelectorAll(
      '#stack-discounts-container .stack-discounts-tag[data-is-automatic="0"]'
    )
  );
}

export function hasActiveDiscountYardCoupon() {
  return getManualDiscountYardTags().length > 0;
}

/** Códigos dos cupons manuais aplicados no DY. */
function getDiscountYardCodes() {
  return getManualDiscountYardTags()
    .map(tag => (tag.getAttribute('data-value') || tag.textContent || '').trim())
    .filter(Boolean);
}

/**
 * Mesma operação do orchestrator: preenche o campo do DY e envia pelo form/botão dele.
 * @param {string} code
 * @returns {boolean} false se o widget do DY ainda não está na página
 */
function submitToDiscountYard(code) {
  const input = document.getElementById('coupons_stacker_input');
  const button = document.getElementById('coupons_stacker_add_button');
  if (!(input instanceof HTMLInputElement)) return false;

  unblockDiscountyardRequests();
  input.value = code;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));

  const form = input.closest('form');
  if (form) {
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  } else if (button instanceof HTMLElement) {
    button.click();
  }
  return true;
}

/**
 * Guarda do orchestrator (enforceManualCouponRemovalOnAutomaticDiscount):
 * com desconto por linha no carrinho, remove do DY os cupons manuais que
 * não começam com um prefixo cumulativo.
 * @param {string[]} iscaPrefixes
 */
function removeNonCumulativeDiscountYardCoupons(iscaPrefixes) {
  getManualDiscountYardTags().forEach(tag => {
    const code = (tag.getAttribute('data-value') || tag.textContent || '').trim();
    if (rules.startsWithAny(code, iscaPrefixes)) return;
    const closeButton = tag.querySelector('.stack-discounts-close-tag');
    if (closeButton instanceof HTMLElement) closeButton.click();
  });
}

// ── Cookie discount_value (escrito pelo próprio DY) ────────────────────

/**
 * Valor do desconto do DY em reais — mesma leitura de
 * assets/cart-discount-calculator.js, que é como o /cart mostra o total.
 * @returns {number}
 */
export function getDiscountValueCookie() {
  try {
    for (const cookie of document.cookie.split(';')) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'discount_value') return parseFloat(value || '0') || 0;
    }
  } catch {
    // ambiente sem document.cookie — ignora.
  }
  return 0;
}

/**
 * O DY grava o cookie num path que não sabemos — expira nos candidatos.
 * Usado também por assets/cart-clear-button.js.
 */
export function clearStaleDiscountValueCookie() {
  try {
    const expire = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';
    document.cookie = `discount_value=; ${expire}; path=/;`;
    document.cookie = `discount_value=; ${expire}; path=${window.location.pathname};`;
    document.cookie = `discount_value=; ${expire};`;
  } catch {
    // ambiente sem document.cookie — ignora.
  }
}

/**
 * Códigos aplicados no carrinho segundo o próprio Shopify (/cart.js), base de
 * cada envio de `discount`. A lista das pílulas não serve: cupom de frete fica
 * no carrinho sem virar desconto antes do checkout (sem pílula), e mandar só
 * as pílulas o apagaria.
 * @param {string[]} fallback códigos das pílulas, se /cart.js falhar
 * @returns {Promise<string[]>}
 */
async function fetchAppliedDiscountCodes(fallback) {
  try {
    const response = await fetch('/cart.js');
    if (!response.ok) return fallback;
    const cart = await response.json();
    if (!Array.isArray(cart.discount_codes)) return fallback;
    return cart.discount_codes
      .filter((/** @type {{ applicable?: boolean }} */ discount) => discount.applicable !== false)
      .map((/** @type {{ code: string }} */ discount) => discount.code)
      .filter(Boolean);
  } catch {
    return fallback;
  }
}

// ── Pílula de cupom (mesma marcação das nativas, renderizadas no servidor) ─

const CHIP_REMOVE_ICON_PATH =
  'M6.2501 3.7499L3.7499 6.2501M3.7499 3.7499L6.2501 6.2501M9.167 5C9.167 7.30137 7.30137 9.167 5 9.167C2.69863 9.167 0.833 7.30137 0.833 5C0.833 2.69863 2.69863 0.833 5 0.833C7.30137 0.833 9.167 2.69863 9.167 5Z';

/**
 * @param {string} code
 * @param {string} removeAttribute atributo do botão de remover (valor = código)
 * @param {string} removeAriaLabel "Remover cupom" traduzido
 * @returns {HTMLElement}
 */
function buildCouponChip(code, removeAttribute, removeAriaLabel) {
  const chip = document.createElement('span');
  chip.className = 'zrzs-quick-add-coupon__chip';

  const codeEl = document.createElement('span');
  codeEl.className = 'zrzs-quick-add-coupon__chip-code';
  codeEl.textContent = code;

  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'zrzs-quick-add-coupon__chip-remove';
  remove.setAttribute(removeAttribute, code);
  remove.setAttribute('aria-label', `${removeAriaLabel} ${code}`);
  remove.innerHTML = `<svg viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="${CHIP_REMOVE_ICON_PATH}" stroke="currentColor" stroke-linecap="round"/></svg>`;

  chip.append(codeEl, remove);
  return chip;
}

// ── Controlador por painel ─────────────────────────────────────────────

/**
 * Cupom de um painel (overlay ou drawer). Criado pelo CartPanelController
 * (assets/zrzs-quick-add-cart-panel.js), que repassa o HTML da section a
 * cada mutação e chama `afterCartRender` depois de cada render.
 */
export class CouponController {
  /**
   * @param {object} options
   * @param {HTMLElement} options.root elemento do painel
   * @param {string} options.sourceId mesmo sourceId do CartPanelController
   * @param {string} options.sectionId section do conteúdo do carrinho (Section Rendering)
   * @param {(html: string) => Promise<any>} options.renderCartSection aplica o HTML da section no painel
   * @param {() => void} [options.onDiscountYardChange] o DY aplicou/removeu um cupom (o valor só muda no cookie dele)
   */
  constructor({ root, sourceId, sectionId, renderCartSection, onDiscountYardChange }) {
    this.root = root;
    this.sourceId = sourceId;
    this.sectionId = sectionId;
    this.renderCartSection = renderCartSection;
    this.onDiscountYardChange = onDiscountYardChange || (() => {});

    this.form = root.querySelector('[data-zqao-coupon-form]');
    this.input = root.querySelector('[data-zqao-coupon-input]');
    this.submitButton = root.querySelector('[data-zqao-coupon-submit]');
    this.error = root.querySelector('[data-zqao-coupon-error]');
    this.discountYardChips = root.querySelector('[data-zqao-dy-chips]');
    this.messages = this.readMessages();

    /** @type {ReturnType<typeof setTimeout> | null} */
    this.spinnerTimer = null;
    /** @type {string | null} cupom enviado ao DY esperando tag ou mensagem do app. */
    this.pendingDiscountYardCode = null;
    /** Mensagem do DY no momento do envio — uma igual não é resposta a este envio. */
    this.discountYardMessageAtSubmit = '';
    /** @type {string | null} assinatura das tags do DY no último espelho. */
    this.discountYardSignature = null;
    /** @type {(() => void) | null} */
    this.stopWatchingDiscountYard = null;
  }

  connect() {
    this.form?.addEventListener('submit', this.handleSubmit);
    this.input?.addEventListener('input', this.clearError);
    this.root.addEventListener('click', this.handleRemoveClick);
    this.stopWatchingDiscountYard = watchDiscountYard(this.syncDiscountYard);
  }

  disconnect() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.input?.removeEventListener('input', this.clearError);
    this.root.removeEventListener('click', this.handleRemoveClick);
    this.stopWatchingDiscountYard?.();
    if (this.spinnerTimer) clearTimeout(this.spinnerTimer);
  }

  /**
   * Espelha o widget do DY no painel: pílulas dos cupons aplicados, fim do
   * "carregando" quando a tag do cupom enviado aparece, e a mensagem de erro
   * do app no erro do campo. Mudou o conjunto de cupons → onDiscountYardChange
   * (o desconto do DY só muda no cookie dele; o resumo precisa re-renderizar).
   */
  syncDiscountYard = () => {
    const codes = getDiscountYardCodes();
    this.renderDiscountYardChips(codes);

    if (this.pendingDiscountYardCode) {
      const pendingUpper = this.pendingDiscountYardCode.toUpperCase();
      const message = getDiscountYardMessage();
      if (codes.some(code => code.toUpperCase() === pendingUpper)) {
        this.finishDiscountYardSubmit();
      } else if (message && message !== this.discountYardMessageAtSubmit) {
        this.finishDiscountYardSubmit();
        this.showError(message);
      }
    }

    const signature = codes.join('|');
    if (this.discountYardSignature !== null && signature !== this.discountYardSignature) {
      this.onDiscountYardChange();
    }
    this.discountYardSignature = signature;
  };

  /** @param {string[]} codes */
  renderDiscountYardChips(codes) {
    if (!this.discountYardChips) return;
    this.discountYardChips.replaceChildren(
      ...codes.map(code =>
        buildCouponChip(code, 'data-zqao-dy-coupon-remove', this.messages.removeAriaLabel)
      )
    );
  }

  finishDiscountYardSubmit() {
    this.pendingDiscountYardCode = null;
    if (this.spinnerTimer) clearTimeout(this.spinnerTimer);
    this.spinnerTimer = null;
    this.setLoading(false);
  }

  /**
   * Mensagens traduzidas (snippets/zrzs-quick-add-coupon.liquid, data-messages)
   * sobre os padrões das regras e do painel.
   * `codeError` e `shippingError` só existem nessa tradução.
   * @returns {typeof rules.DEFAULT_MESSAGES & typeof PANEL_MESSAGES & { codeError?: string, shippingError?: string }}
   */
  readMessages() {
    /** @type {{ codeError?: string, shippingError?: string }} */
    let translated = {};
    try {
      const raw = this.form instanceof HTMLElement ? this.form.dataset.messages : '';
      if (raw) translated = JSON.parse(raw);
    } catch {
      // sem traduções: fica com os padrões (pt-BR).
    }
    return { ...rules.DEFAULT_MESSAGES, ...PANEL_MESSAGES, ...translated };
  }

  /** Prefixos (settings do tema) e estado do carrinho (região "coupons", renderizada no servidor). */
  readContext() {
    const formData = this.form instanceof HTMLElement ? this.form.dataset : {};
    const state = this.root.querySelector('[data-zqao-coupon-state]');
    const stateData = state instanceof HTMLElement ? state.dataset : {};
    return {
      nativePrefixes: rules.parsePrefixList(formData.nativePrefixes || ''),
      iscaPrefixes: rules.parsePrefixList(formData.iscaPrefixes || ''),
      hasLineLevelDiscounts: stateData.hasLineLevelDiscounts === 'true',
      activeNativeCodes: (stateData.nativeCodes || '')
        .split(',')
        .map(code => code.trim())
        .filter(Boolean),
    };
  }

  /** Há cupom nativo ativo no carrinho (decide CTA de checkout e se o DY aparece). */
  hasActiveNativeCoupon() {
    return this.readContext().activeNativeCodes.length > 0;
  }

  /** @param {SubmitEvent} event */
  handleSubmit = async event => {
    event.preventDefault();
    if (!(this.input instanceof HTMLInputElement)) return;
    const code = this.input.value.trim();
    if (!code || this.submitButton?.hasAttribute('disabled')) return;

    this.clearError();
    const context = this.readContext();
    const result = rules.validateCoupon(code, context, this.messages);
    if (!result.ok) {
      this.showError(result.message);
      return;
    }

    if (result.route === 'native') {
      await this.applyNativeCoupon(code, context.activeNativeCodes);
    } else {
      this.applyDiscountYardCoupon(code);
    }
  };

  /**
   * Como assets/cart-discount.js#applyDiscount: acumula com os nativos já
   * aplicados e bloqueia o DY.
   * @param {string} code
   * @param {string[]} activeNativeCodes
   */
  async applyNativeCoupon(code, activeNativeCodes) {
    const codeUpper = code.toUpperCase();
    if (activeNativeCodes.some(active => active.toUpperCase() === codeUpper)) {
      this.showError(this.messages.alreadyApplied);
      return;
    }

    this.setLoading(true);
    blockDiscountyardRequests();
    try {
      const appliedCodes = await fetchAppliedDiscountCodes(activeNativeCodes);
      const data = await this.updateDiscountCodes([...appliedCodes, code]);
      const applied = (data.discount_codes || []).find(
        (/** @type {{ code: string }} */ discount) =>
          discount.code?.toUpperCase() === codeUpper
      );

      if (!applied || applied.applicable === false) {
        // Shopify mantém o código "não aplicável" na lista — tira de novo,
        // como o /cart (que não renderiza pill pra ele).
        await this.updateDiscountCodes(appliedCodes);
        this.showError(this.messages.codeError || this.messages.requestFailed);
        return;
      }

      if (this.input instanceof HTMLInputElement) this.input.value = '';
      // Aplicável mas não virou desconto no carrinho: cupom de frete (só no
      // checkout, como no /cart). Continua no carrinho — fetchAppliedDiscountCodes
      // o mantém nos próximos envios.
      if (!this.readContext().activeNativeCodes.some(active => active.toUpperCase() === codeUpper)) {
        this.showError(this.messages.shippingError || this.messages.requestFailed);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[QuickAddCoupon] Erro ao aplicar cupom nativo:', error);
      this.showError(this.messages.requestFailed);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Como o orchestrator: entrega pro widget do DY e deixa a UI dele (tags e
   * mensagens, visíveis no painel) mostrar o resultado.
   * @param {string} code
   */
  applyDiscountYardCoupon(code) {
    if (!submitToDiscountYard(code)) {
      this.showError(this.messages.discountYardUnavailable);
      return;
    }
    if (this.input instanceof HTMLInputElement) this.input.value = '';
    this.pendingDiscountYardCode = code;
    this.discountYardMessageAtSubmit = getDiscountYardMessage();
    this.setLoading(true);
    if (this.spinnerTimer) clearTimeout(this.spinnerTimer);
    // Rede de segurança — normalmente a tag ou a mensagem do app chega antes.
    // Mesma mensagem de antes (ex.: o mesmo cupom inválido de novo) só é
    // percebida aqui, quando o tempo acaba sem tag.
    this.spinnerTimer = setTimeout(() => {
      const message = getDiscountYardMessage();
      this.finishDiscountYardSubmit();
      if (message) this.showError(message);
    }, DISCOUNTYARD_SPINNER_MS);
  }

  /**
   * Remover pílula: DY clica no "x" do app; nativo como cart-discount.js#removeDiscount.
   * @param {MouseEvent} event
   */
  handleRemoveClick = async event => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const discountYardButton = target.closest('[data-zqao-dy-coupon-remove]');
    if (discountYardButton instanceof HTMLButtonElement) {
      discountYardButton.disabled = true;
      this.clearError();
      unblockDiscountyardRequests();
      removeDiscountYardCoupon(discountYardButton.getAttribute('data-zqao-dy-coupon-remove') || '');
      return;
    }

    const button = target.closest('[data-zqao-native-coupon-remove]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const code = button.getAttribute('data-zqao-native-coupon-remove') || '';

    button.disabled = true;
    this.clearError();
    unblockDiscountyardRequests();
    try {
      const appliedCodes = await fetchAppliedDiscountCodes(
        this.readContext().activeNativeCodes
      );
      const remaining = appliedCodes.filter(
        applied => applied.toUpperCase() !== code.toUpperCase()
      );
      await this.updateDiscountCodes(remaining);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[QuickAddCoupon] Erro ao remover cupom nativo:', error);
      button.disabled = false;
      this.showError(this.messages.requestFailed);
    }
  };

  /**
   * /cart/update.js com a lista final de códigos nativos + HTML do carrinho
   * (Section Rendering); aplica o HTML e avisa os outros painéis.
   * @param {string[]} codes
   * @returns {Promise<any>} resposta do /cart/update.js
   */
  async updateDiscountCodes(codes) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: codes.join(','),
        sections: this.sectionId,
        sections_url: window.location.pathname,
      }),
    });
    if (!response.ok) throw new Error(`status ${response.status}`);
    const data = await response.json();

    const sectionHtml = data.sections?.[this.sectionId];
    if (sectionHtml) await this.renderCartSection(sectionHtml);
    document.dispatchEvent(new DiscountUpdateEvent(data, this.sourceId));
    return data;
  }

  /**
   * Depois de cada render do carrinho: CTA de checkout e visibilidade do DY
   * seguem o nativo ativo (como has-troca-discount no /cart), pílulas do DY
   * e a guarda dos cupons não cumulativos.
   */
  afterCartRender() {
    const context = this.updateNativeCouponState();
    this.syncDiscountYard();
    if (context.hasLineLevelDiscounts) {
      removeNonCumulativeDiscountYardCoupons(context.iscaPrefixes);
    }
  }

  /**
   * `data-zqao-has-native-coupon` no painel: troca o CTA de checkout e
   * esconde o DY (como has-troca-discount no /cart).
   */
  updateNativeCouponState() {
    const context = this.readContext();
    this.root.toggleAttribute(
      'data-zqao-has-native-coupon',
      context.activeNativeCodes.length > 0
    );
    return context;
  }

  /** O acordeão de cupom abriu: o widget do DY pode ter carregado depois do último render. */
  handlePanelShown() {
    this.syncDiscountYard();
  }

  /** @param {string} message */
  showError(message) {
    if (this.error) this.error.textContent = message;
    this.root.querySelector('.zrzs-quick-add-coupon')?.setAttribute('data-state', 'denied');
  }

  clearError = () => {
    if (this.error) this.error.textContent = '';
    this.root.querySelector('.zrzs-quick-add-coupon')?.setAttribute('data-state', 'idle');
  };

  /** @param {boolean} loading */
  setLoading(loading) {
    if (!(this.submitButton instanceof HTMLButtonElement)) return;
    this.submitButton.disabled = loading;
    this.submitButton.classList.toggle('is-loading', loading);
    this.submitButton.setAttribute('aria-busy', String(loading));
  }
}
